<template>
  <div class="department-manager">
    <!-- Department Section -->
    <div class="departments-section">
      <v-expansion-panels>
        <v-expansion-panel
          v-for="department in departments"
          :key="department.id"
          class="mb-2"
        >
          <v-expansion-panel-title>
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-icon color="primary" size="20" class="mr-2">
                  mdi-office-building
                </v-icon>
                <v-text-field
                  v-if="editingDepartment === department.id"
                  v-model="department.name"
                  @blur="saveDepartmentName(department)"
                  @keyup.enter="saveDepartmentName(department)"
                  :rules="nameRules"
                  maxlength="100"
                  hide-details
                  dense
                  class="edit-department-field mt-0 pt-0"
                ></v-text-field>
                <span v-else @dblclick="startEditingDepartment(department.id)">
                  {{ department.name }}
                </span>
              </div>
              <div class="d-flex align-center">
                <v-btn
                  icon
                  density="comfortable"
                  variant="text"
                  color="primary"
                  @click.stop="startEditingDepartment(department.id)"
                >
                  <v-icon size="18">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  density="comfortable"
                  variant="text"
                  color="error"
                  @click.stop="confirmDeleteDepartment(department)"
                >
                  <v-icon size="18">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <!-- Projects Section -->
            <div class="projects-section">
              <div class="d-flex justify-space-between align-center mb-4">
                <v-btn
                  color="primary"
                  class="add-btn"
                  @click="openNewProjectDialog(department.id)"
                >
                  <v-icon left class="mr-2">mdi-plus</v-icon>
                  Add Project
                </v-btn>
              </div>

              <div class="projects-container">
                <v-card
                  v-for="project in department.projects"
                  :key="project.id"
                  class="mb-3"
                  variant="outlined"
                >
                  <v-card-text class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-icon color="primary" size="20" class="mr-2">
                        mdi-folder-outline
                      </v-icon>
                      <v-text-field
                        v-if="editingProject === project.id"
                        v-model="project.name"
                        @blur="saveProjectName(department.id, project)"
                        @keyup.enter="saveProjectName(department.id, project)"
                        :rules="nameRules"
                        maxlength="100"
                        hide-details
                        dense
                        class="edit-project-field mt-0 pt-0"
                      ></v-text-field>
                      <span v-else @dblclick="startEditingProject(project.id)">
                        {{ project.name }}
                      </span>
                    </div>
                    <div class="d-flex align-center">
                      <v-btn
                        icon
                        density="comfortable"
                        variant="text"
                        color="primary"
                        @click.stop="startEditingProject(project.id)"
                      >
                        <v-icon size="18">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        density="comfortable"
                        variant="text"
                        color="error"
                        @click.stop="confirmDeleteProject(department.id, project)"
                      >
                        <v-icon size="18">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>

                  <!-- Workshops Section -->
                  <v-card-text>
                    <div class="d-flex justify-space-between align-center mb-4">
                      <v-btn
                        color="primary"
                        class="add-btn"
                        @click="openNewWorkshopDialog(project, department.id)"
                      >
                        <v-icon left class="mr-2">mdi-plus</v-icon>
                        Create Initiative
                      </v-btn>
                    </div>

                    <div class="workshops-scroll">
                      <div class="workshops-row">
                        <v-hover
                          v-for="workshop in project.workshops"
                          :key="workshop.id"
                          v-slot="{ isHovering, props }"
                        >
                          <v-card
                            v-bind="props"
                            :elevation="isHovering ? 4 : 1"
                            class="workshop-card mr-4"
                          >
                            <v-card-text
                              class="d-flex align-center justify-space-between pa-4"
                            >
                              <div class="d-flex align-center" @click="handleWorkshopClick(workshop.id)">
                                <v-icon size="20" color="primary" class="mr-2">
                                  mdi-file-document-outline
                                </v-icon>
                                <v-text-field
                                  v-if="editingWorkshop === workshop.id"
                                  v-model="workshop.name"
                                  @blur="saveWorkshopName(workshop, department.id, project.id)"
                                  @keyup.enter="saveWorkshopName(workshop, department.id, project.id)"
                                  :rules="nameRules"
                                  maxlength="100"
                                  @click.stop
                                  hide-details
                                  dense
                                  class="edit-workshop-field mt-0 pt-0"
                                ></v-text-field>
                                <span v-else @dblclick="startEditingWorkshop(workshop.id)">
                                  {{ workshop.name }}
                                </span>
                                <div v-if="workshopRiskScores[workshop.id]" class="d-flex align-center mt-2" style="gap: 8px;">
                                  <v-chip size="small" :color="workshopRiskScores[workshop.id].overallColor">
                                    Overall Risk: {{ workshopRiskScores[workshop.id].overall }}
                                  </v-chip>
                                  <v-chip size="small" :color="workshopRiskScores[workshop.id].initiativeColor">
                                    Initiative Risk: {{ workshopRiskScores[workshop.id].initiative }}
                                  </v-chip>
                                </div>
                              </div>
                              <div class="d-flex align-center">
                                <v-btn
                                  icon
                                  density="comfortable"
                                  variant="text"
                                  color="primary"
                                  @click.stop="startEditingWorkshop(workshop.id)"
                                >
                                  <v-icon size="18">mdi-pencil</v-icon>
                                </v-btn>
                                <v-btn
                                  icon
                                  density="comfortable"
                                  variant="text"
                                  color="error"
                                  @click.stop="confirmDeleteWorkshop(workshop, project, department.id)"
                                >
                                  <v-icon size="18">mdi-delete</v-icon>
                                </v-btn>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-hover>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-btn
        color="primary"
        class="add-btn"
        @click="openNewDepartmentDialog"
      >
        <v-icon left class="mr-2">mdi-plus</v-icon>
        Add Department
      </v-btn>
    </div>

    <!-- New Department Dialog -->
    <v-dialog v-model="newDepartmentDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 pa-4">
          Create a new Department
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newDepartmentName"
            label="Department Name"
            :rules="nameRules"
            maxlength="100"
            hide-details
            class="mb-4"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="newDepartmentDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="createDepartment"
            :disabled="!isValidName(newDepartmentName)"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New Project Dialog -->
    <v-dialog v-model="newProjectDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 pa-4">
          Create a new Project
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newProjectName"
            label="Project Name"
            :rules="nameRules"
            maxlength="100"
            hide-details
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="justify-end pa-4">
          <v-btn
            variant="text"
            color="grey-darken-1"
            @click="newProjectDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="createProject"
            :disabled="!isValidName(newProjectName) || !selectedDepartmentId"
          >
            Create Project
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New Workshop Dialog -->
    <v-dialog v-model="newWorkshopDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 pa-4">
          Create a new Initiative
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newWorkshopName"
            label="Initiative Name"
            :rules="nameRules"
            maxlength="100"
            hide-details
            class="mb-4"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="justify-end pa-4">
          <v-btn
            variant="text"
            color="grey-darken-1"
            @click="newWorkshopDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="createWorkshop"
            :disabled="!isValidName(newWorkshopName) || !selectedProject || !selectedDepartmentId"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 pa-4">
          {{ deleteDialogTitle }}
        </v-card-title>
        <v-card-text class="pa-4">
          {{ deleteDialogMessage }}
        </v-card-text>
        <v-card-actions class="justify-end pa-4">
          <v-btn
            variant="text"
            color="grey-darken-1"
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  createProject as createProjectApi,
  deleteProject,
  updateProjectName,
  updateDepartmentName,
  deleteDepartment,
  deleteWorkshop,
  updateWorkshopName,
  createDepartment as createDepartmentApi
} from '@/workshop/departmentFunctions';
import { createWorkshop as createWorkshopApi, loadWorkshop } from '@/workshop/workshopFunctions';
import { calculateCompositeRiskScore } from '../utils/riskScore';

export default {
  name: 'DepartmentProjectManager',
  props: {
    customerId: {
      type: String,
      required: true
    },
    departments: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      editingDepartment: null,
      editingProject: null,
      editingWorkshop: null,
      nameRules: [
        v => !!(v && String(v).trim()) || 'Name is required',
        v => (String(v || '').trim().length >= 3) || 'Min. 3 characters',
        v => (String(v || '').trim().length <= 100) || 'Max. 100 characters'
      ],
      newProjectDialog: false,
      newProjectName: '',
      selectedDepartmentId: null,
      newWorkshopDialog: false,
      newWorkshopName: '',
      newWorkshopCompanyName: '',
      selectedProject: null,
      deleteDialog: false,
      deleteDialogTitle: '',
      deleteDialogMessage: '',
      itemToDelete: null,
      deleteType: null, // 'department', 'project', or 'workshop'
      newDepartmentDialog: false,
      newDepartmentName: '',
      workshopRiskScores: {} // { [workshopId]: { overall: number, initiative: number, color: string } }
    };
  },
  mounted() {
    this.fetchAllWorkshopRiskScores();
  },
  watch: {
    departments: {
      handler() { this.fetchAllWorkshopRiskScores(); },
      deep: true
    }
  },
  methods: {
    // Department methods
    startEditingDepartment(departmentId) {
      if (this.editingDepartment === departmentId) {
        this.clearAllEditing();
      } else {
        this.clearAllEditing();
        this.editingDepartment = departmentId;
      }
    },

    async saveDepartmentName(department) {
      if (this.editingDepartment === department.id) {
        if (!this.isValidName(department.name)) return;
        department.name = department.name.trim().slice(0, 100);
        try {
          await updateDepartmentName(this.customerId, department.id, department.name);
          this.$emit('department-updated');
        } catch (error) {
          console.error('Error updating department name:', error);
        }
        this.editingDepartment = null;
      }
    },

    confirmDeleteDepartment(department) {
      this.deleteType = 'department';
      this.deleteDialogTitle = 'Delete Department';
      this.deleteDialogMessage = `Are you sure you want to delete department "${department.name}" and all its projects? This action cannot be undone.`;
      this.itemToDelete = department;
      this.deleteDialog = true;
    },

    openNewDepartmentDialog() {
      this.newDepartmentDialog = true;
    },

    async createDepartment() {
      if (this.newDepartmentName.trim()) {
        if (!this.isValidName(this.newDepartmentName)) return;
        try {
          await createDepartmentApi(this.customerId, this.newDepartmentName);
          await this.$emit('department-created');
          this.newDepartmentDialog = false;
          this.newDepartmentName = '';
        } catch (error) {
          console.error('Error creating department:', error);
        }
      }
    },

    // Project methods
    startEditingProject(projectId) {
      if (this.editingProject === projectId) {
        this.clearAllEditing();
      } else {
        this.clearAllEditing();
        this.editingProject = projectId;
      }
    },

    async saveProjectName(departmentId, project) {
      if (this.editingProject === project.id) {
        if (!this.isValidName(project.name)) return;
        project.name = project.name.trim().slice(0, 100);
        try {
          await updateProjectName(this.customerId, departmentId, project.id, project.name);
          this.$emit('project-updated');
        } catch (error) {
          console.error('Error updating project name:', error);
        }
        this.editingProject = null;
      }
    },

    openNewProjectDialog(departmentId) {
      this.selectedDepartmentId = departmentId;
      this.newProjectDialog = true;
    },

    async createProject() {
      if (!this.newProjectName.trim() || !this.selectedDepartmentId) {
        return;
      }

      if (!this.isValidName(this.newProjectName)) return;

      try {
        // Create project
        await createProjectApi(
          this.customerId,
          this.selectedDepartmentId,
          this.newProjectName.trim()
        );

        // Reset form and close dialog
        this.newProjectName = '';
        this.newProjectDialog = false;

        // Emit event to refresh data
        this.$emit('project-created');
      } catch (error) {
        console.error('Error creating project:', error);
      }
    },

    confirmDeleteProject(departmentId, project) {
      this.deleteType = 'project';
      this.deleteDialogTitle = 'Delete Project';
      this.deleteDialogMessage = `Are you sure you want to delete project "${project.name}" and all its initiatives? This action cannot be undone.`;
      this.itemToDelete = { ...project, departmentId };
      this.deleteDialog = true;
    },

    // Workshop methods
    startEditingWorkshop(workshopId) {
      if (this.editingWorkshop === workshopId) {
        this.clearAllEditing();
      } else {
        this.clearAllEditing();
        this.editingWorkshop = workshopId;
      }
    },

    async saveWorkshopName(workshop, departmentId, projectId) {
      if (this.editingWorkshop === workshop.id) {
        if (!this.isValidName(workshop.name)) return;
        workshop.name = workshop.name.trim().slice(0, 100);
        try {
          await updateWorkshopName(this.customerId, departmentId, projectId, workshop.id, workshop.name);
          this.$emit('workshop-updated');
        } catch (error) {
          console.error('Error updating workshop name:', error);
        }
        this.editingWorkshop = null;
      }
    },
    handleWorkshopClick(workshopId) {
      if (this.editingWorkshop) return; // prevent navigation when editing
      this.$emit('open-workshop', this.customerId, workshopId);
    },
    clearAllEditing() {
      this.editingDepartment = null;
      this.editingProject = null;
      this.editingWorkshop = null;
    },

    openNewWorkshopDialog(project, departmentId) {
      this.selectedProject = project;
      this.selectedDepartmentId = departmentId;
      this.newWorkshopDialog = true;
      this.newWorkshopName = '';
      this.newWorkshopCompanyName = '';
    },

    async createWorkshop() {
      if (!this.newWorkshopName.trim() || !this.selectedProject || !this.selectedDepartmentId) {
        return;
      }

      if (!this.isValidName(this.newWorkshopName)) return;

      try {
        // Create workshop
        await createWorkshopApi(
          this.customerId,
          this.selectedDepartmentId,
          this.selectedProject.id,
          '', // company name not required in dialog; derive elsewhere if needed
          this.newWorkshopName.trim()
        );

        // Reset form and close dialog
        this.newWorkshopName = '';
        this.selectedProject = null;
        this.selectedDepartmentId = null;
        this.newWorkshopDialog = false;

        // Emit event to refresh data
        this.$emit('workshop-created');
      } catch (error) {
        console.error('Error creating workshop:', error);
      }
    },

    confirmDeleteWorkshop(workshop, project, departmentId) {
      this.deleteType = 'workshop';
      this.deleteDialogTitle = 'Delete Initiative';
      this.deleteDialogMessage = `Are you sure you want to delete this initiative "${workshop.name}"? This action cannot be undone.`;
      this.itemToDelete = { ...workshop, projectId: project.id, departmentId };
      this.deleteDialog = true;
    },

    // Delete confirmation
    async confirmDelete() {
      try {
        if (this.deleteType === 'department') {
          await deleteDepartment(this.customerId, this.itemToDelete.id);
          this.$emit('department-deleted');
        } else if (this.deleteType === 'project') {
          await deleteProject(this.customerId, this.itemToDelete.departmentId, this.itemToDelete.id);
          this.$emit('project-deleted');
        } else if (this.deleteType === 'workshop') {
          await deleteWorkshop(
            this.customerId, 
            this.itemToDelete.departmentId,
            this.itemToDelete.projectId,
            this.itemToDelete.id
          );
          this.$emit('workshop-deleted');
        }
        this.deleteDialog = false;
        this.itemToDelete = null;
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    },

    async fetchAllWorkshopRiskScores() {
      if (!Array.isArray(this.departments)) {
        this.workshopRiskScores = {};
        return;
      }
      const allWorkshops = [];
      for (const dept of this.departments) {
        for (const proj of dept.projects || []) {
          for (const ws of proj.workshops || []) {
            allWorkshops.push(ws);
          }
        }
      }
      const scores = {};
      for (const ws of allWorkshops) {
        scores[ws.id] = await this.fetchWorkshopRiskScore(ws);
      }
      this.workshopRiskScores = scores;
    },

    async fetchWorkshopRiskScore(workshop) {
      try {
        const data = await loadWorkshop(this.customerId, workshop.id);
        const results = data.results || {};
        const selectedTopics = data.selectedTopics || [];
        // Build aspects array
        const aspects = Object.entries(results).map(([id, res]) => ({
          id,
          voteWhereWeAre: Number(res.voteWhereWeAre),
          voteWhereWantToBe: Number(res.voteWhereWantToBe)
        }));
        // Overall risk score (all voted topics)
        let overall = 0;
        if (aspects.length) {
          overall = aspects.reduce((sum, a) => sum + calculateCompositeRiskScore(a.voteWhereWeAre, a.voteWhereWantToBe), 0) / aspects.length;
        }
        // Initiative risk score (selected topics)
        let initiative = 0;
        if (selectedTopics.length) {
          const selectedAspects = aspects.filter(a => selectedTopics.includes(a.id));
          if (selectedAspects.length) {
            initiative = selectedAspects.reduce((sum, a) => sum + calculateCompositeRiskScore(a.voteWhereWeAre, a.voteWhereWantToBe), 0) / selectedAspects.length;
          }
        }
        // Color
        const getColor = (score) => score > 5 ? 'error' : score >= 3 ? 'warning' : 'success';
        return {
          overall: overall.toFixed(2),
          initiative: initiative.toFixed(2),
          overallColor: getColor(overall),
          initiativeColor: getColor(initiative)
        };
      } catch (e) {
        return { overall: '0.00', initiative: '0.00', overallColor: 'grey', initiativeColor: 'grey' };
      }
    },

    isValidName(name) {
      const n = String(name || '').trim();
      return n.length >= 3 && n.length <= 100;
    }
  }
};
</script>

<style scoped>
.edit-department-field {
  /* Expand inside expansion-panel title; avoid single-letter width */
  flex: 1 1 480px;
  width: clamp(260px, 40vw, 620px);
  min-width: 260px;
  max-width: 620px;
}
.edit-project-field {
  /* Comfortable inline width in project row */
  width: clamp(220px, 30vw, 420px);
  min-width: 220px;
}
.edit-workshop-field {
  /* Fit neatly inside workshop card without pushing action icons */
  flex: 0 0 clamp(140px, 18vw, 180px);
  width: clamp(140px, 18vw, 180px);
  min-width: 140px;
  max-width: 180px;
}
.department-manager {
  width: 100%;
}

.projects-container {
  margin-top: 1rem;
}

.workshop-card {
  min-width: 200px;
  max-width: 300px;
  cursor: pointer;
}

.workshops-scroll {
  overflow-x: auto;
  padding-bottom: 1rem;
}

.workshops-row {
  display: flex;
  flex-wrap: nowrap;
}

.workshop-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
